import { useEffect } from 'react'
import { onAuthStateChange, useUIStore, verifyRecoveryToken } from '@restaurant/core'
import { isShowcase } from '../showcase/showcase'

/**
 * Opens the auth sheet in 'set-new-password' mode when the user arrives from
 * a password-reset email. Two arrival shapes are supported:
 *
 * 1. Query-token links — `?token_hash=...&type=recovery` (what the gotrue
 *    recovery template sends since 2026-08-08). The token is not consumed by
 *    `detectSessionInUrl`, so it's exchanged explicitly via
 *    `verifyRecoveryToken`, which establishes the session `updatePassword`
 *    needs. The params are stripped afterwards so a refresh doesn't re-verify
 *    a now-consumed token.
 *
 * 2. Hash-fragment tokens — Supabase parses these on load
 *    (`detectSessionInUrl: true`) and fires PASSWORD_RECOVERY itself,
 *    regardless of which route the link lands on.
 *
 * Either path flags the UI store; layout orchestration opens the sheet.
 * Mount once at the App root.
 */
export function useRecoveryRedirect(): void {
  useEffect(() => {
    if (isShowcase()) return

    const params = new URLSearchParams(window.location.search)
    const tokenHash = params.get('token_hash')
    if (tokenHash && params.get('type') === 'recovery') {
      // Strip the token from the URL before the async exchange: it's
      // single-use, so keeping it around only invites double-verification.
      params.delete('token_hash')
      params.delete('type')
      const query = params.toString()
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      )

      verifyRecoveryToken(tokenHash)
        .then(() => {
          useUIStore.getState().requestRecovery()
        })
        .catch((error) => {
          // Expired/used link. The user can request a fresh email from the
          // auth sheet; nothing actionable beyond that.
          console.warn('Password-recovery token verification failed', error)
        })
    }

    const subscription = onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        useUIStore.getState().requestRecovery()
      }
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])
}
